<template>
  <transition :name="isModal ? 'modal' : ''">
    <md-card
      :class="containerClasses"
      :style="isModal ? 'margin-top: 0; margin-bottom: 0; background: rgba(0, 0, 0, .5);' : ''"
    >
      <div :class="wrapperClasses">
        <form @submit.prevent>
          <md-card class="md-card-wizard active" data-color="purple">
            <md-card-header>
              <slot name="header">
                <h3 class="card-title">{{ computedTitle }}</h3>
                <h3 class="description">{{ computedSubTitle }}</h3>
                <hr v-if="!isModal" style="margin-top: 20px" />
              </slot>
            </md-card-header>

            <div class="wizard-navigation">
              <ul class="nav nav-pills" role="tablist">
                <li
                  v-for="(tab, index) in tabs"
                  :key="tab.title"
                  :tabindex="tab.checked ? 0 : ''"
                  :id="`step-${tab.tabId}`"
                  :aria-controls="tab.tabId"
                  :aria-disabled="tab.active"
                  :aria-selected="tab.active"
                  :ref="`tab-${index}`"
                  :style="linkWidth"
                  role="tab"
                  class="nav-item wizard-tab-link"
                >
                  <a
                    :class="[
                      { 'disabled-wizard-link': !tab.checked },
                      { active: tab.active },
                      { checked: tab.checked }
                    ]"
                    class="nav-link"
                    data-toggle="tab"
                    @click="navigateToTab(index)"
                  >
                    <tab-item-content :tab="tab" />
                  </a>
                </li>
              </ul>

              <div
                v-if="activeTab"
                :class="{ 'error-link': activeTab.hasError }"
                :style="movingTabStyles"
                class="moving-tab"
                style="transition: transform 0.5s cubic-bezier(0.29, 1.42, 0.79, 1); width: 100%"
              >
                <tab-item-content :tab="activeTab" :moving-tab="true" />
              </div>
            </div>

            <md-card-content>
              <div class="tab-content">
                <slot :activeIndex="activeTabIndex" :activeTab="activeTab" />
              </div>
            </md-card-content>

            <md-card-actions md-alignment="space-between">
              <slot :next-tab="nextTab" :prev-tab="prevTab" name="footer">
                <div>
                  <md-button v-if="activeTabIndex > 0" class="btn-previous" @click.native="prevTab">
                    {{ computedPrevButtonText }}
                  </md-button>

                  <md-button v-if="activeTabIndex == 0" class="btn-previous" @click.native="exitTab">
                    {{ computedExitButtonText }}
                  </md-button>
                </div>

                <div>
                  <md-button v-if="activeTabIndex < tabCount - 1" class="btn-next md-primary" @click.native="nextTab">
                    {{ computedNextButtonText }}
                  </md-button>

                  <md-button v-else class="md-success" @click.native="nextTab">
                    {{ computedFinishButtonText }}
                  </md-button>
                </div>
              </slot>
            </md-card-actions>
          </md-card>
        </form>
      </div>
    </md-card>
  </transition>
</template>

<script>
import { throttle } from "./throttle"

export default {
  name: "SimpleWizard",
  components: {
    TabItemContent: {
      props: ["tab", "movingTab"],
      render(h) {
        return h("span", [this.tab.$slots.label || this.tab.label])
      }
    }
  },
  props: {
    startIndex: {
      type: Number,
      default: 0
    },
    title: {
      type: String,
      default: "Title"
    },
    subTitle: {
      type: String,
      default: "Subtitle"
    },
    exitButtonText: {
      type: String,
      default: "Exit"
    },
    prevButtonText: {
      type: String,
      default: "Previous"
    },
    nextButtonText: {
      type: String,
      default: "Next"
    },
    finishButtonText: {
      type: String,
      default: "Finish"
    },
    vertical: {
      type: Boolean
    },
    goNext: {
      type: Boolean
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  provide() {
    return {
      addTab: this.addTab,
      removeTab: this.removeTab
    }
  },
  data() {
    return {
      tabs: [],
      activeTabIndex: 0,
      tabLinkWidth: 0,
      tabLinkHeight: 50
    }
  },
  computed: {
    tabCount() {
      return this.tabs.length
    },
    linkWidth() {
      let width = 100
      if (this.tabCount > 0) {
        width = 100 / this.tabCount
      }
      if (this.vertical) {
        width = 100
      }
      return { width: `${width}%` }
    },
    activeTab() {
      return this.tabs[this.activeTabIndex]
    },
    movingTabStyles() {
      let translateXValue =
        this.activeTabIndex == 0
          ? this.tabLinkWidth * this.activeTabIndex - 8
          : this.activeTabIndex == this.tabCount - 1
          ? this.tabLinkWidth * this.activeTabIndex + 8
          : this.tabLinkWidth * this.activeTabIndex

      let translateYValue = 0
      if (this.vertical) {
        translateYValue = this.tabLinkHeight * this.activeTabIndex
        translateXValue = 0
      }
      let styles = {
        transform: `translate3d(${translateXValue}px, ${translateYValue}px, 0px)`
      }
      if (this.tabLinkWidth !== 0) {
        styles.width = `${this.tabLinkWidth}px`
      }
      return styles
    },
    computedTitle() {
      return this.isEdit ? "Edit Doctor" : this.title
    },
    computedSubTitle() {
      return this.isEdit ? "Update doctor details" : this.subTitle
    },
    computedExitButtonText() {
      return this.isEdit ? "Cancel Edit" : this.exitButtonText
    },
    computedPrevButtonText() {
      return this.isEdit ? "Back" : this.prevButtonText
    },
    computedNextButtonText() {
      return this.isEdit ? "Continue Editing" : this.nextButtonText
    },
    computedFinishButtonText() {
      return this.isEdit ? "Save Changes" : this.finishButtonText
    },
    containerClasses() {
      return {
        "modal-mask": this.isModal,
        "wizard-container": !this.isModal
      }
    },
    wrapperClasses() {
      return {
        "modal-container": this.isModal,
        "page-container": !this.isModal
      }
    }
  },
  watch: {
    activeTabIndex(newValue, oldValue) {
      if (newValue !== oldValue) {
        let oldTab = this.tabs[oldValue]
        let newTab = this.tabs[newValue]
        oldTab.active = false
        newTab.active = true

        if (!newTab.checked) {
          newTab.checked = true
        }
        this.$emit("tab-change", oldTab, newTab)
        this.$emit("update:startIndex", newValue)
      }
    },
    goNext(newValue) {
      if (newValue) {
        this.nextTab()
      }
    }
  },
  mounted() {
    this.activeTabIndex = this.startIndex
    this.$nextTick(() => {
      this.tabs[this.activeTabIndex].active = true
      this.tabs[this.activeTabIndex].checked = true
      this.onResize()
    })
    window.addEventListener(
      "resize",
      () => {
        throttle(this.onResize, 40)
      },
      false
    )
  },
  methods: {
    addTab(tab) {
      const index = this.$slots.default.indexOf(tab.$vnode)
      let tabTitle = tab.title || ""
      tab.tabId = `${tabTitle.replace(/ /g, "")}${index}`
      if (!this.activeTab && index === 0) {
        tab.active = true
        tab.checked = true
      }
      if (this.activeTab === tab.name) {
        tab.active = true
        tab.checked = true
      }
      this.tabs.splice(index, 0, tab)
    },
    removeTab(tab) {
      const tabs = this.tabs
      const index = tabs.indexOf(tab)
      if (index > -1) {
        tabs.splice(index, 1)
      }
    },
    validate(tab) {
      let tabToValidate = tab || this.activeTab
      let beforeChange = tabToValidate.beforeChange
      if (beforeChange) {
        return Promise.resolve(beforeChange())
          .then((res) => {
            this.activeTab.hasError = res ? false : true
            return res
          })
          .catch(() => {
            this.activeTab.hasError = true
          })
      } else {
        return Promise.resolve(true)
      }
    },
    async nextTab() {
      let isValid = await this.validate()
      if (isValid && this.activeTabIndex < this.tabCount - 1) {
        this.activeTabIndex++
      }
      return isValid
    },
    prevTab() {
      this.activeTabIndex--
    },
    exitTab() {
      if (!this.isModal) {
        this.$router.push("/")
      } else {
        this.$emit("close")
      }
    },
    async navigateToTab(index) {
      if (this.tabs[index].checked) {
        // recursively validate each tab
        if (index > this.activeTabIndex) {
          let valid = await this.nextTab()
          if (valid) {
            this.navigateToTab(index)
          }
        } else {
          this.activeTabIndex = index
        }
      }
    },
    onResize() {
      let tabLinks = document.getElementsByClassName("wizard-tab-link")
      if (tabLinks.length > 0 && this.tabCount > 0) {
        let { clientWidth, clientHeight } = tabLinks[0]
        this.tabLinkWidth = clientWidth
        this.tabLinkHeight = clientHeight
      }
    }
  }
}
</script>
<style lang="scss" scoped>
/* Tab content animation */
.tab-content {
  display: flex; // to avoid horizontal scroll when animating
  .tab-pane {
    display: block;
    animation: fadeIn 0.5s;
    width: 100%;
  }
}

@media only screen and (max-width: 736px) {
  .md-select-menu .md-list-item .md-list-item-content {
    min-height: 25px;
  }
  .md-field .md-input .md-textarea {
    padding: 12px 0 0 0;
  }

  .moving-tab {
    margin-left: 16px !important;
  }

  .md-card .md-card-content {
    padding: 11px 10px !important;
  }

  .md-card-wizard .tab-content {
    padding: 10px !important;
  }

  .spaced-phone-label {
    top: -15px !important;
  }

  .dense-phone-input {
    padding-bottom: 0 !important;
  }

  .space-right-phone {
    margin-right: 17px;
  }

  #step-4 {
    a {
      padding: 6px 15px 0 0 !important;
    }
  }

  #state-select {
    .md-select {
      .md-icon {
        svg {
          margin-left: 5px;
        }
      }
    }
  }

  .wizard-container {
    padding-top: 5px;
  }
}

/**
    Extra niceties. Display error tabs and disable navigation unvisited tabs
   */
.wizard-navigation .nav-link {
  &.active,
  &.checked {
    cursor: pointer;
  }
}

.disabled-wizard-link {
  cursor: not-allowed;
}
/* Modal Styles */
.modal-mask {
  position: fixed;
  margin: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.md-card {
  margin: 0 !important;
}
.modal-container {
  max-width: 70vw;
  max-height: 75vh !important;
  max-width: 46.5rem;
  overflow: auto;
  margin: 0px auto;
  position: relative;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 27px 24px 0 rgba(0, 0, 0, 0.2), 0 40px 77px 0 rgba(0, 0, 0, 0.22);
  border-radius: 6px;
  border: none;
  z-index: 9997;
}
.modal-body {
  min-width: 30rem;
}
.modal-enter,
.modal-leave-active {
  opacity: 0;
}
.modal-enter .modal-container,
.modal-leave-active .modal-container {
  transform: scale(1.1);
}
</style>
